import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, FileText, Users, Briefcase, TrendingUp, DollarSign, Plus } from 'lucide-react';
import { clientesApi, empleadosApi, serviciosApi, relacionesApi } from '../api/endpoints';
import { Spinner } from '../components/ui/Spinner';

export function Dashboard() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ clientes: 0, empleados: 0, servicios: 0, relaciones: 0, total: 0 });

  useEffect(() => {
    (async () => {
      try {
        const [clientes, empleados, servicios] = await Promise.all([
          clientesApi.list(),
          empleadosApi.list(),
          serviciosApi.list(),
        ]);

        let totalRel = 0;
        let totalMonto = 0;
        for (const c of clientes) {
          const rels = await relacionesApi.list(c.id);
          totalRel += rels.length;
          totalMonto += rels.reduce((acc, r) => acc + (r.total || 0), 0);
        }

        setStats({
          clientes: clientes.length,
          empleados: empleados.length,
          servicios: servicios.length,
          relaciones: totalRel,
          total: totalMonto,
        });
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Spinner size={36} />
      </div>
    );
  }

  const cards = [
    { label: 'Clientes', value: stats.clientes, icon: Building2, color: 'cyan', to: '/clientes' },
    { label: 'Empleados', value: stats.empleados, icon: Users, color: 'emerald', to: '/empleados' },
    { label: 'Servicios Catálogo', value: stats.servicios, icon: Briefcase, color: 'amber', to: '/servicios' },
    { label: 'Relaciones Soporte', value: stats.relaciones, icon: FileText, color: 'indigo', to: '/relaciones' },
  ];

  return (
    <div className="p-3 sm:p-6 space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Dashboard</h1>
          <p className="text-sm text-muted mt-0.5">Resumen de actividad del sistema</p>
        </div>
        <Link
          to="/relaciones/nueva"
          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white text-sm font-semibold rounded-lg shadow-lg shadow-cyan-500/20"
        >
          <Plus size={16} /> Nueva Relación
        </Link>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <Link
            key={c.label}
            to={c.to}
            className="bg-panel border border-border rounded-xl p-5 hover:border-cyan-500/40 transition group"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-muted tracking-wider">{c.label.toUpperCase()}</p>
                <p className="mt-2 text-3xl font-bold text-white">{c.value}</p>
              </div>
              <div className={`w-10 h-10 rounded-lg bg-${c.color}-500/10 flex items-center justify-center group-hover:bg-${c.color}-500/20`}>
                <c.icon size={20} className={`text-${c.color}-400`} />
              </div>
            </div>
          </Link>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-panel border border-border rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <DollarSign size={18} className="text-emerald-400" />
            <h3 className="font-semibold text-white">Ingresos Generados</h3>
          </div>
          <p className="text-4xl font-bold text-emerald-400">
            ${stats.total.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
          <p className="text-xs text-muted mt-2">MXN · Total acumulado de relaciones generadas</p>
        </div>

        <div className="bg-panel border border-border rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp size={18} className="text-cyan-400" />
            <h3 className="font-semibold text-white">Accesos Rápidos</h3>
          </div>
          <div className="space-y-2">
            <Link to="/clientes" className="block px-3 py-2 rounded-md bg-panel-2 hover:bg-border text-sm text-slate-300 hover:text-white">Ver clientes</Link>
            <Link to="/relaciones" className="block px-3 py-2 rounded-md bg-panel-2 hover:bg-border text-sm text-slate-300 hover:text-white">Relaciones de soporte</Link>
            <Link to="/servicios" className="block px-3 py-2 rounded-md bg-panel-2 hover:bg-border text-sm text-slate-300 hover:text-white">Catálogo de servicios</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
